import { FieldProps, useField } from 'informed';
import React from 'react';
import Select, { PropsValue, GroupBase, StylesConfig, components } from 'react-select';
import { StateManagerProps } from 'react-select/dist/declarations/src/stateManager';

import './TyInput.css';
import CustomOption from './CustomOption';
import { InputWrapper } from '@tymbe/ui-forms';

type Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = StateManagerProps<
  Option,
  IsMulti,
  Group
>;

type DivProps = { label?: string & React.HTMLProps<HTMLDivElement> };

type TyMultiSelectProps<Option, Fields extends object, IsMulti extends boolean, Group extends GroupBase<Option>> = Omit<
  Props<Option, IsMulti, Group>,
  keyof FieldProps<DivProps, Option, Fields>
> &
  FieldProps<DivProps, Option, Fields>;

const TyMultiSelect = <Fields extends object, Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: TyMultiSelectProps<Option, Fields, IsMulti, Group>
) => {
  const { fieldState, userProps, fieldApi, render, ref } = useField<typeof props, PropsValue<Option>>({ ...props });
  const {
    id,
    label,
    className,
    placeholder = ' ',
    options,
    isClearable = false,
    noOptionsMessage = () => 'Žádné výsledky',
    loadingMessage = () => 'Načítání',
    isDisabled = false,
  } = userProps;
  const { error, showError, value } = fieldState;
  const { setValue, setTouched, setFocused } = fieldApi;

  const customComponentStyles: StylesConfig = {
    placeholder: (styles) => ({ ...styles, padding: 0, margin: 0, minHeight: 0 }),
    input: (styles) => ({ ...styles, padding: 0, margin: 0, minHeight: 0, outline: 'none' }),
    control: (styles) => ({ ...styles, borderWidth: 0, padding: 0, margin: 0, minHeight: 0, boxShadow: 'none' }),
    valueContainer: (styles) => ({ ...styles, margin: 0, padding: 0, height: 'fit-content', minHeight: 0 }),
    multiValue: (styles) => ({ ...styles, borderRadius: '20px', padding: '0 4px', overflow: 'hidden' }),
  };

  return render(
    <InputWrapper className={className} label={label} error={showError ? String(error) : undefined}>
      <Select
        ref={ref}
        styles={customComponentStyles}
        isMulti
        inputId={id}
        placeholder={placeholder}
        className="w-full"
        components={{
          Option: CustomOption,
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        onChange={(e) => setValue(e?.length ? e : undefined)}
        onBlur={(e) => {
          setFocused(false, e);
          setTouched(true, e);
        }}
        onFocus={(e) => setFocused(true, e)}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={loadingMessage}
        maxMenuHeight={250}
        isClearable={isClearable}
        isDisabled={isDisabled}
        options={options}
        value={value}
        aria-invalid={showError}
        aria-describedby={`${id}-error`}
      />
    </InputWrapper>
  );
};

export default TyMultiSelect as typeof TyMultiSelect;
